import { Table, TableCell, TableRow, TableBody, TableHeader } from '@lmig/lmds-react-table';
import { IconRemoveItem } from '@lmig/lmds-react-icons';
import { Select, SelectOption } from '@lmig/lmds-react-select';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { TablePagination } from '@mui/material';
import { GridRow, GridCol } from '@lmig/lmds-react-grid';
import { useMemo, useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import { DefaultTextFilterField, DefaultDropdownFilterField } from './PortalTableFilters';
import { FilterTagsFromTable } from './FilterTagsFromTable';

interface PortalUsersTableProps {
  portalUsers: IPortalUser[];
  isUpdating: boolean | undefined;
  isDisabling: boolean | undefined;
  user: string | undefined;
  onUpdateRole: (userId: string, role: string) => void;
  onDisableUser: (userId: string) => void;
  pagination: { page: number; pageSize: number };
  totalRecords: number;
  sorting: { sortBy: string; sortOrder: 'asc' | 'desc' };
  filters: Record<string, string | string[]>;
  onPaginationChange: (pagination: { page: number; pageSize: number }) => void;
  onSortingChange: (sorting: { sortBy: string; sortOrder: 'asc' | 'desc' }) => void;
  onFiltersChange: (filters: Record<string, string | string[]>) => void;
  isLoadingData?: boolean;
}

export function PortalUsersTable({
  portalUsers,
  isUpdating,
  isDisabling,
  user,
  onUpdateRole,
  onDisableUser,
  pagination,
  totalRecords,
  sorting,
  filters,
  onPaginationChange,
  onSortingChange,
  onFiltersChange,
  isLoadingData = false,
}: PortalUsersTableProps) {
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1250);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const FormatDateString = (dateString: string) => {
    let dateFormatted = '';
    try {
      const date = new Date(dateString);
      dateFormatted = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    } catch (error) {}
    return dateFormatted;
  };

  const columns = useMemo<ColumnDef<IPortalUser>[]>(
    () => [
      {
        header: 'User ID',
        accessorKey: 'userId',
        id: 'userId',
        enableColumnFilter: true,
      },
      {
        header: 'First name',
        accessorKey: 'firstName',
        id: 'firstName',
        enableColumnFilter: true,
      },
      {
        header: 'Last name',
        accessorKey: 'lastName',
        id: 'lastName',
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: 'Email address',
        accessorKey: 'emailAddress',
        id: 'emailAddress',
        enableColumnFilter: true,
      },
      {
        header: 'Role',
        accessorKey: 'role',
        id: 'role',
        filterFn: 'arrIncludesSome',
        enableColumnFilter: true,
        cell: ({ row }) => (
          <Select
            loading={isUpdating && user === row.original.userId}
            onChange={(event: React.FormEvent<HTMLSelectElement>) =>
              onUpdateRole(row.original.userId, event.currentTarget?.value)
            }
            labelVisual=""
            className="role-drop-down"
            value={row.original.role}
          >
            <SelectOption value="ADMIN">Admin</SelectOption>
            <SelectOption value="USER">User</SelectOption>
          </Select>
        ),
      },
      {
        header: 'Last access date',
        accessorKey: 'lastAccessDate',
        id: 'lastAccessDate',
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => FormatDateString(row.original.lastAccessDate),
      },
      {
        header: 'Disable*',
        id: 'disable',
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div onClick={() => onDisableUser(row.original.userId)} className="disable-user-button">
            {isDisabling && row.original.userId === user ? (
              <CircularProgressSpinner
                className="portal-users-loading-spinner disabling-spinner"
                size="16"
                aria-label="Disabling user..."
              />
            ) : (
              <>
                <IconRemoveItem size="16" />
                Disable
              </>
            )}
          </div>
        ),
      },
    ],
    [isUpdating, isDisabling, user, onUpdateRole, onDisableUser]
  );

  const data = useMemo(() => portalUsers, [portalUsers]);

  // Convert filters object to TanStack Table format
  const columnFilters = useMemo(() => {
    return Object.keys(filters).map(key => ({
      id: key,
      value: filters[key]
    }));
  }, [filters]);

  // Convert sorting to TanStack Table format
  const sortingState = useMemo(() => {
    if (!sorting.sortBy) return [];
    return [{
      id: sorting.sortBy,
      desc: sorting.sortOrder === 'desc'
    }];
  }, [sorting]);

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      enableColumnFilter: true,
    },
    state: {
      pagination: {
        pageIndex: pagination.page - 1, // TanStack uses 0-based, we use 1-based
        pageSize: pagination.pageSize,
      },
      columnFilters,
      sorting: sortingState,
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: Math.ceil(totalRecords / pagination.pageSize),
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' 
        ? updater({ pageIndex: pagination.page - 1, pageSize: pagination.pageSize })
        : updater;
      onPaginationChange({ 
        page: newPagination.pageIndex + 1, // Convert back to 1-based
        pageSize: newPagination.pageSize 
      });
    },
    onColumnFiltersChange: (updater) => {
      const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
      const filtersObj: Record<string, string | string[]> = {};
      newFilters.forEach(filter => {
        filtersObj[filter.id] = filter.value as string | string[];
      });
      onFiltersChange(filtersObj);
      // Reset to page 1 when filters change
      onPaginationChange({ ...pagination, page: 1 });
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sortingState) : updater;
      if (newSorting.length > 0) {
        onSortingChange({
          sortBy: newSorting[0].id,
          sortOrder: newSorting[0].desc ? 'desc' : 'asc'
        });
      } else {
        onSortingChange({ sortBy: '', sortOrder: 'asc' });
      }
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="manage-portal-users-content">
      <GridRow gutters>
        <GridCol base={12}>
          <FilterTagsFromTable tableInstance={table} />
        </GridCol>
        {isMobileView && (
          <GridCol base={12}>
            <div className="mobile-filters-panel">
              <h3 className="mobile-filters-title">Filters</h3>
              <GridRow gutterSize="fixed-16" gutters>
                {table.getAllColumns().filter(col => col.getCanFilter()).map((column) => (
                  <GridCol key={column.id} base={12} sm={6} md={4}>
                    <div className="mobile-filter-field">
                      <label className="mobile-filter-label">{column.columnDef.header as string}</label>
                      {column.columnDef.filterFn === 'arrIncludesSome' ? (
                        <DefaultDropdownFilterField 
                          column={column} 
                          table={table} 
                          customFilterChoices={column.id === 'role' ? ['ADMIN', 'USER'] : undefined}
                        />
                      ) : (
                        <DefaultTextFilterField column={column} table={table} />
                      )}
                    </div>
                  </GridCol>
                ))}
              </GridRow>
            </div>
          </GridCol>
        )}
        <GridCol base={12}>
          <Table className="manage-portal-users-table" rowStyle="zebra" spacing="compact">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const sortState = header.column.getIsSorted();
                    const sortDirection = sortState === 'asc' ? 'ascending' : sortState === 'desc' ? 'descending' : 'none';
                    
                    return (
                      <TableCell 
                        key={header.id} 
                        type="colHead"
                        isSortable={header.column.getCanSort()}
                        sortDirection={sortDirection}
                        onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {!isMobileView && table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={`${headerGroup.id}-filters`}>
                  {headerGroup.headers.map((header) => {
                    const { column } = header;
                    let filterField;
                    if (!column.getCanFilter()) {
                      filterField = null;
                    } else if (column?.columnDef.filterFn === 'arrIncludesSome') {
                      filterField = (
                        <DefaultDropdownFilterField 
                          column={column} 
                          table={table} 
                          customFilterChoices={column.id === 'role' ? ['ADMIN', 'USER'] : undefined}
                        />
                      );
                    } else {
                      filterField = <DefaultTextFilterField column={column} table={table} />;
                    }
                    return <TableCell key={header.id}>{filterField}</TableCell>;
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoadingData ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                    <CircularProgressSpinner
                      className="portal-users-loading-spinner"
                      size="32"
                      aria-label="Loading portal users data..."
                    />
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                    <div style={{ color: '#666' }}>
                      No users found matching the current filters.
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            className="table-pagination"
            component="div"
            count={totalRecords}
            rowsPerPage={pagination.pageSize}
            page={pagination.page - 1}
            rowsPerPageOptions={[]}
            onPageChange={(_, newPage) => onPaginationChange({ ...pagination, page: newPage + 1 })}
          />
          <p className="disable-helper-text">
            * Disabling an ID will also disable access to the Surety Portal and the Liberty Mutual Online Portal if
            applicable.
          </p>
        </GridCol>
      </GridRow>
    </div>
  );
}
